import React, { useEffect, useRef, useState } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from '../firestore-compat';
import './TemaTela.css';

// Cores que cada pessoa escolhe para a Minha Agenda e o E-mail. Ficam no cadastro do usuário
// (users.data.preferencias.<tela>) — valem em qualquer computador — e numa cópia local para abrir
// a tela já com as cores certas.
export const TEMAS = [
  { nome: 'Claro (padrão)', fundo: '#f4f7f6', destaque: '#00897b' },
  { nome: 'Azul clarinho', fundo: '#eaf3fb', destaque: '#2563eb' },
  { nome: 'Verde menta', fundo: '#e7f6ef', destaque: '#0f9d76' },
  { nome: 'Lilás', fundo: '#f1edfb', destaque: '#7c3aed' },
  { nome: 'Rosa', fundo: '#fcedf3', destaque: '#db2777' },
  { nome: 'Pêssego', fundo: '#fdf1e7', destaque: '#ea580c' },
  { nome: 'Areia', fundo: '#f5f0e6', destaque: '#a16207' },
  { nome: 'Grafite', fundo: '#1f2937', destaque: '#22c3a6' },
];
export const TEMA_PADRAO = TEMAS[0];

/** Fundo escuro pede texto claro (e o contrário). */
export function textoPara(cor) {
  const m = String(cor || '').match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return '#1f2937';
  const [r, g, b] = m.slice(1).map(h => parseInt(h, 16) / 255).map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.4 ? '#f8fafc' : '#1f2937';
}

const chaveLocal = tela => `quality_tema_${tela}`;
function lerLocal(tela) {
  try { return JSON.parse(localStorage.getItem(chaveLocal(tela)) || 'null'); } catch { return null; }
}

/** Tema da tela para o usuário logado: { tema, salvar(novo) } */
export function useTemaTela(tela) {
  const [tema, setTema] = useState(() => ({ ...TEMA_PADRAO, ...(lerLocal(tela) || {}) }));
  const docId = useRef(null);
  const prefs = useRef({});
  useEffect(() => {
    const email = auth.currentUser?.email;
    if (!email) return;
    let ativo = true;
    getDocs(query(collection(db, 'users'), where('email', '==', email.toLowerCase()))).then(snap => {
      if (!ativo || snap.empty) return;
      docId.current = snap.docs[0].id;
      prefs.current = snap.docs[0].data().preferencias || {};
      const salvo = prefs.current[tela];
      if (salvo?.fundo) {
        setTema({ ...TEMA_PADRAO, ...salvo });
        try { localStorage.setItem(chaveLocal(tela), JSON.stringify(salvo)); } catch { /* sem armazenamento */ }
      }
    }).catch(() => {});
    return () => { ativo = false; };
  }, [tela]);
  const salvar = async novo => {
    const t = { fundo: novo.fundo, destaque: novo.destaque };
    setTema({ ...TEMA_PADRAO, ...t });
    try { localStorage.setItem(chaveLocal(tela), JSON.stringify(t)); } catch { /* sem armazenamento */ }
    if (docId.current) {
      prefs.current = { ...prefs.current, [tela]: t };
      // grava só a chave desta tela — as outras preferências (ex.: ✨ Visual da barra do topo) ficam como estão no cadastro
      await updateDoc(doc(db, 'users', docId.current), { [`preferencias.${tela}`]: t }).catch(() => {});
    }
  };
  return { tema, salvar };
}

/** Botão "🎨 Cores" com a paleta: temas prontos + cor do fundo e de destaque à escolha. */
export function SeletorTema({ tema, onSalvar, claro }) {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState(tema);
  const caixa = useRef(null);
  useEffect(() => { if (aberto) setRascunho(tema); }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = e => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);
  const aplicar = t => { setRascunho(t); onSalvar(t); };
  return (
    <div className="tt-rel" ref={caixa}>
      <button type="button" className={`tt-botao ${claro ? 'tt-botao-claro' : ''}`} onClick={() => setAberto(a => !a)} title="Personalizar as cores desta tela">🎨 Cores</button>
      {aberto && (
        <div className="tt-painel">
          <div className="tt-titulo">Cores desta tela <span>só para você</span></div>
          <div className="tt-temas">
            {TEMAS.map(t => {
              const ativo = t.fundo.toLowerCase() === String(rascunho.fundo).toLowerCase() && t.destaque.toLowerCase() === String(rascunho.destaque).toLowerCase();
              return (
                <button type="button" key={t.nome} className={`tt-tema ${ativo ? 'ativo' : ''}`} onClick={() => aplicar(t)} title={t.nome}>
                  <span className="tt-amostra" style={{ background: t.fundo }}><i style={{ background: t.destaque }} /></span>
                  <span className="tt-nome">{t.nome}</span>
                </button>
              );
            })}
          </div>
          <div className="tt-proprias">
            <label>Fundo<input type="color" value={rascunho.fundo} onChange={e => setRascunho(r => ({ ...r, fundo: e.target.value }))} onBlur={() => onSalvar(rascunho)} /></label>
            <label>Destaque<input type="color" value={rascunho.destaque} onChange={e => setRascunho(r => ({ ...r, destaque: e.target.value }))} onBlur={() => onSalvar(rascunho)} /></label>
            <button type="button" className="tt-salvar" onClick={() => { onSalvar(rascunho); setAberto(false); }}>Salvar</button>
          </div>
          <button type="button" className="tt-restaurar" onClick={() => aplicar(TEMA_PADRAO)}>Voltar ao padrão</button>
        </div>
      )}
    </div>
  );
}
